import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LogIn, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth, formatApiError } from "../context/AuthContext";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [leagueName, setLeagueName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        await register(name, email, password, leagueName);
      } else {
        await login(email, password);
      }
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err, mode === "register" ? "Registreringen misslyckades" : "Inloggningen misslyckades"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-[#FAFAFC] flex flex-col">
      <header className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto w-full py-6">
        <Link to="/" data-testid="login-back-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft size={15} /> Tillbaka till startsidan
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <div className="bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 shadow-xl shadow-slate-900/5" data-testid="login-card">
            <div className="flex items-center gap-3 mb-8">
              <Logo size={40} />
              <div>
                <p className="font-display font-bold text-lg leading-tight">Fantasy Bet</p>
                <p className="text-xs text-slate-500">{mode === "register" ? "Skapa konto" : "Logga in"}</p>
              </div>
            </div>

            <h1 className="font-display font-bold text-2xl tracking-tight mb-2">
              {mode === "register" ? "Skapa din liga" : "Välkommen tillbaka"}
            </h1>
            <p className="text-sm text-slate-500 mb-8">
              {mode === "register" ? "Registrera dig och skapa din liga – du blir automatiskt admin." : "Logga in för att hantera dina ligor."}
            </p>

            {error && (
              <div data-testid="login-error" className="mb-6 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3">
                {error}
              </div>
            )}

            <form onSubmit={submit} className="space-y-5">
              {mode === "register" && (
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 block mb-2">Namn</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="register-name-input"
                    className="w-full bg-[#FAFAFC] border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
                    placeholder="Ditt namn"
                  />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 block mb-2">E-post</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="w-full bg-[#FAFAFC] border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
                  placeholder="din@epost.se"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 block mb-2">Lösenord</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full bg-[#FAFAFC] border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
                  placeholder="••••••••"
                />
              </div>
              {mode === "register" && (
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 block mb-2">Namn på ligan</label>
                  <input
                    type="text"
                    required
                    value={leagueName}
                    onChange={(e) => setLeagueName(e.target.value)}
                    data-testid="register-league-input"
                    className="w-full bg-[#FAFAFC] border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
                    placeholder="T.ex. Kompisligan 2026"
                  />
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-xl transition-colors"
              >
                {mode === "register" ? <UserPlus size={17} /> : <LogIn size={17} />}
                {loading ? (mode === "register" ? "Skapar konto..." : "Loggar in...") : (mode === "register" ? "Skapa konto" : "Logga in")}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                data-testid="toggle-auth-mode"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                {mode === "register" ? "Har du redan ett konto? Logga in" : "Inget konto? Registrera dig"}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
